<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api } from "@/services/api";

type WorkflowKey =
  | "stages"
  | "required"
  | "outputs"
  | "acceptance"
  | "forbidden";
type Workflow = Record<WorkflowKey, string[]>;
type SkillRow = {
  id: string;
  name: string;
  domain: string;
  description: string;
  prompt_template: string;
  workflow_json: string;
  enabled: number;
  status: string;
  updated_at: string;
};
type SkillDraft = Omit<SkillRow, "workflow_json"> & { workflow: Workflow };
type AgentTool = {
  id: string;
  name: string;
  description: string;
  input_schema: string;
  lifecycle_policy: string;
  handler_key: string;
  enabled: number;
};
type Revision = {
  revision_number: number;
  name: string;
  domain: string;
  description: string;
  enabled: number;
  published_at: string;
};

const blankWorkflow = (): Workflow => ({
  stages: [],
  required: [],
  outputs: [],
  acceptance: [],
  forbidden: [],
});
const blankDraft = (): SkillDraft => ({
  id: "",
  name: "",
  domain: "general",
  description: "",
  prompt_template: "",
  workflow: blankWorkflow(),
  enabled: 1,
  status: "draft",
  updated_at: "",
});
const skills = ref<SkillRow[]>([]);
const tools = ref<AgentTool[]>([]);
const draft = ref<SkillDraft>(blankDraft());
const selectedId = ref("");
const creating = ref(false);
const saving = ref(false);
const revisions = ref<Revision[]>([]);
const testMessage = ref("");
const testing = ref(false);
const testResult = ref<any>(null);
const workflowGroups: Array<{ key: WorkflowKey; label: string; hint: string }> =
  [
    { key: "stages", label: "标准阶段", hint: "按执行顺序填写" },
    { key: "required", label: "必问信息", hint: "缺少时 Agent 必须追问" },
    { key: "outputs", label: "标准交付物", hint: "用于拆解任务与交付" },
    { key: "acceptance", label: "验收标准", hint: "任务完成的判断依据" },
    {
      key: "forbidden",
      label: "禁止假设",
      hint: "例如 AI 视频不得默认真人实拍",
    },
  ];
const selected = computed(() =>
  skills.value.find((skill) => skill.id === selectedId.value),
);

function parseWorkflow(value: string): Workflow {
  try {
    return { ...blankWorkflow(), ...JSON.parse(value || "{}") };
  } catch {
    return blankWorkflow();
  }
}
function editSkill(skill: SkillRow) {
  selectedId.value = skill.id;
  creating.value = false;
  testResult.value = null;
  draft.value = { ...skill, workflow: parseWorkflow(skill.workflow_json) };
  void loadRevisions(skill.id);
}
function createSkill() {
  selectedId.value = "";
  creating.value = true;
  revisions.value = [];
  testResult.value = null;
  draft.value = blankDraft();
}
function addWorkflowItem(key: WorkflowKey) {
  draft.value.workflow[key].push("");
}
function removeWorkflowItem(key: WorkflowKey, index: number) {
  draft.value.workflow[key].splice(index, 1);
}
async function load() {
  const [skillResult, toolResult] = await Promise.all([
    api<{ skills: SkillRow[] }>("/admin/skills"),
    api<{ tools: AgentTool[] }>("/admin/tools"),
  ]);
  skills.value = skillResult.skills;
  tools.value = toolResult.tools;
  if (!selectedId.value && skills.value[0]) editSkill(skills.value[0]);
  else if (selected.value) editSkill(selected.value);
}
async function loadRevisions(id: string) {
  revisions.value = (
    await api<{ revisions: Revision[] }>(`/admin/skills/${id}/revisions`)
  ).revisions;
}
async function save() {
  saving.value = true;
  try {
    const body = {
      name: draft.value.name,
      domain: draft.value.domain,
      description: draft.value.description,
      promptTemplate: draft.value.prompt_template,
      workflow: draft.value.workflow,
      enabled: Boolean(draft.value.enabled),
    };
    if (creating.value)
      await api("/admin/skills", {
        method: "POST",
        body: JSON.stringify({ id: draft.value.id, ...body }),
      });
    else
      await api(`/admin/skills/${draft.value.id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
    selectedId.value = draft.value.id;
    creating.value = false;
    await load();
  } finally {
    saving.value = false;
  }
}
async function runTest() {
  if (!draft.value.id || !testMessage.value.trim()) return;
  testing.value = true;
  testResult.value = null;
  try {
    testResult.value = (
      await api<{ result: any }>(`/admin/skills/${draft.value.id}/test`, {
        method: "POST",
        body: JSON.stringify({ message: testMessage.value }),
      })
    ).result;
  } finally {
    testing.value = false;
  }
}
async function rollback(revision: number) {
  if (!confirm(`确定回滚到 v${revision} 吗？`)) return;
  await api(`/admin/skills/${draft.value.id}/rollback/${revision}`, {
    method: "POST",
  });
  await load();
}
onMounted(() => {
  void load();
});
</script>

<template>
  <div class="skill-admin">
    <header class="page-head">
      <div>
        <h1>Agent Skills</h1>
        <p>管理业务流程、提示词、工具能力、测试和发布版本。</p>
      </div>
      <button class="btn btn--primary" @click="createSkill">新建 Skill</button>
    </header>
    <section class="tool-band">
      <header>
        <div>
          <h2>可调用工具</h2>
          <p>工具由后端适配器注册，Skill 只能在允许的生命周期内调用。</p>
        </div>
        <b>{{ tools.filter((tool) => tool.enabled).length }} 个已启用</b>
      </header>
      <div class="tool-grid">
        <article v-for="tool in tools" :key="tool.id">
          <div>
            <code>{{ tool.id }}</code
            ><b>{{ tool.name }}</b>
          </div>
          <p>{{ tool.description }}</p>
          <footer>
            <span>{{ tool.lifecycle_policy }}</span
            ><small>{{ tool.handler_key }}</small>
          </footer>
        </article>
      </div>
    </section>
    <div class="workspace">
      <aside class="catalog">
        <button
          v-for="skill in skills"
          :key="skill.id"
          :class="{ active: selectedId === skill.id }"
          @click="editSkill(skill)"
        >
          <span
            ><b>{{ skill.name }}</b
            ><small>{{ skill.description || skill.domain }}</small></span
          ><i :class="{ enabled: skill.enabled }">{{
            skill.enabled ? "启用" : "停用"
          }}</i>
        </button>
      </aside>
      <main class="editor">
        <header>
          <div>
            <h2>{{ creating ? "新建业务 Skill" : draft.name }}</h2>
            <p>核心确认、权限和变更规则由平台锁定；此处配置领域流程。</p>
          </div>
          <label class="toggle"
            ><input
              v-model="draft.enabled"
              :true-value="1"
              :false-value="0"
              type="checkbox"
            />启用</label
          >
        </header>
        <section class="form-grid">
          <label
            >Skill 标识<input
              v-model="draft.id"
              class="input"
              :disabled="!creating"
              placeholder="ai-video-production" /></label
          ><label
            >显示名称<input
              v-model="draft.name"
              class="input"
              placeholder="AI 视频生产" /></label
          ><label
            >业务分类<input
              v-model="draft.domain"
              class="input"
              placeholder="ai_video" /></label
          ><label
            >适用说明<textarea
              v-model="draft.description"
              class="textarea"
              rows="2"
            /></label
          ><label class="full"
            >领域提示词<textarea
              v-model="draft.prompt_template"
              class="textarea"
              rows="8"
            />
          </label>
        </section>
        <section class="workflow">
          <header>
            <h3>标准流程</h3>
            <p>这些结构化规则会随主 Skill 注入 Agent。</p>
          </header>
          <div class="workflow-grid">
            <article v-for="group in workflowGroups" :key="group.key">
              <header>
                <div>
                  <b>{{ group.label }}</b
                  ><small>{{ group.hint }}</small>
                </div>
                <button
                  class="icon-button"
                  :title="`添加${group.label}`"
                  @click="addWorkflowItem(group.key)"
                >
                  +
                </button>
              </header>
              <div
                v-for="(item, index) in draft.workflow[group.key]"
                :key="`${group.key}-${index}`"
                class="workflow-row"
              >
                <input
                  v-model="draft.workflow[group.key][index]"
                  class="input"
                  :aria-label="item || group.label"
                /><button
                  class="icon-button danger"
                  title="删除"
                  @click="removeWorkflowItem(group.key, index)"
                >
                  ×
                </button>
              </div>
              <p v-if="!draft.workflow[group.key].length">尚未配置</p>
            </article>
          </div>
        </section>
        <div class="save-row">
          <button class="btn btn--primary" :disabled="saving" @click="save">
            {{ saving ? "保存中..." : "保存并发布新版本" }}
          </button>
        </div>
        <section v-if="!creating" class="bottom-grid">
          <article class="test-console">
            <h3>真实模型测试</h3>
            <textarea
              v-model="testMessage"
              class="textarea"
              rows="4"
              placeholder="输入一条客户需求，验证此 Skill 的模型输出"
            /><button class="btn" :disabled="testing" @click="runTest">
              {{ testing ? "测试中..." : "运行测试" }}
            </button>
            <pre v-if="testResult">{{
              JSON.stringify(testResult, null, 2)
            }}</pre>
          </article>
          <article class="revisions">
            <h3>修订历史</h3>
            <div v-for="revision in revisions" :key="revision.revision_number">
              <span
                ><b>v{{ revision.revision_number }}</b
                ><small>{{
                  revision.published_at.slice(0, 16).replace("T", " ")
                }}</small></span
              ><button class="btn" @click="rollback(revision.revision_number)">
                回滚
              </button>
            </div>
            <p v-if="!revisions.length">首次保存后生成修订版本。</p>
          </article>
        </section>
      </main>
    </div>
  </div>
</template>

<style scoped>
.skill-admin {
  min-height: calc(100vh - 64px);
  background: #f6f8f8;
}
.page-head,
.tool-band,
.workspace {
  max-width: 1240px;
  margin: auto;
}
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 28px 24px 18px;
}
.page-head h1,
.page-head p,
.tool-band h2,
.tool-band p,
.editor h2,
.editor p,
.workflow h3,
.bottom-grid h3 {
  margin: 0;
}
.page-head p,
.tool-band p,
.editor p,
.workflow p {
  margin-top: 5px;
  color: var(--muted);
}
.tool-band {
  background: #fff;
  border: 1px solid var(--line);
}
.tool-band > header {
  display: flex;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid var(--line);
}
.tool-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.tool-grid article {
  padding: 14px;
  border-right: 1px solid var(--line);
}
.tool-grid article:last-child {
  border-right: 0;
}
.tool-grid article > div {
  display: grid;
  gap: 5px;
}
.tool-grid code {
  color: var(--brand-dark);
  font-size: 11px;
}
.tool-grid p {
  min-height: 40px;
  font-size: 12px;
}
.tool-grid footer {
  display: flex;
  justify-content: space-between;
  margin-top: 9px;
}
.tool-grid footer span {
  padding: 2px 5px;
  background: #eef6fb;
  color: #286788;
  font-size: 11px;
}
.tool-grid footer small {
  color: var(--muted);
}
.workspace {
  display: grid;
  grid-template-columns: 290px minmax(0, 1fr);
  margin-top: 16px;
  border: 1px solid var(--line);
  background: #fff;
}
.catalog {
  padding: 10px;
  border-right: 1px solid var(--line);
  background: #fbfcfc;
}
.catalog button {
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 9px;
  padding: 12px;
  border: 1px solid transparent;
  background: transparent;
  text-align: left;
}
.catalog button.active {
  border-color: #efb88c;
  background: #fff0e7;
}
.catalog span b,
.catalog span small {
  display: block;
}
.catalog span small {
  margin-top: 4px;
  color: var(--muted);
}
.catalog i {
  font-size: 11px;
  font-style: normal;
  color: var(--muted);
}
.catalog i.enabled {
  color: var(--success);
}
.editor {
  padding: 24px;
}
.editor > header {
  display: flex;
  justify-content: space-between;
}
.toggle {
  display: flex;
  gap: 6px;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 18px;
}
.form-grid label {
  display: grid;
  gap: 6px;
  font-weight: 650;
}
.form-grid .full {
  grid-column: 1/-1;
}
.workflow {
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid var(--line);
}
.workflow-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 14px;
}
.workflow-grid article {
  padding: 13px;
  border: 1px solid var(--line);
  background: #fafcfc;
}
.workflow-grid article > header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.workflow-grid small {
  display: block;
  margin-top: 3px;
  color: var(--muted);
}
.workflow-grid article > p {
  font-size: 12px;
}
.workflow-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px;
  margin-top: 7px;
}
.icon-button {
  width: 30px;
  height: 30px;
  border: 1px solid var(--line);
  background: #fff;
}
.icon-button.danger {
  color: #b34f46;
}
.save-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
.bottom-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 22px;
}
.test-console,
.revisions {
  display: grid;
  gap: 10px;
  padding: 15px;
  border: 1px solid var(--line);
}
.test-console pre {
  max-height: 320px;
  overflow: auto;
  margin: 0;
  padding: 10px;
  background: #172126;
  color: #d9e8ed;
  white-space: pre-wrap;
  font-size: 11px;
}
.revisions > div {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--line);
}
.revisions span b,
.revisions span small {
  display: block;
}
.revisions span small {
  color: var(--muted);
}
@media (max-width: 900px) {
  .tool-grid {
    grid-template-columns: 1fr 1fr;
  }
  .workspace {
    grid-template-columns: 1fr;
  }
  .catalog {
    border-right: 0;
    border-bottom: 1px solid var(--line);
  }
  .bottom-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 700px) {
  .page-head {
    padding: 18px 14px;
  }
  .tool-band,
  .workspace {
    margin-left: 14px;
    margin-right: 14px;
  }
  .tool-grid,
  .workflow-grid,
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-grid .full {
    grid-column: auto;
  }
  .editor {
    padding: 16px;
  }
}
</style>
